import { h } from 'preact';
import { useCallback, useState } from 'preact/hooks';
import { memo } from 'preact/compat';

import { useSelector, useDispatch } from 'react-redux';

import { styled } from '@mui/material/styles';

import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

import Divider from '@mui/material/Divider';
import MuiDrawer from '@mui/material/Drawer';

import Outbound from '@mui/icons-material/OutboundRounded';
import Delete from '@mui/icons-material/Delete.js';

import { actions } from '../../store/state/drawer.js';

import ReactiveItemLink from '../ListLink/reactiveItemLink.jsx';
import {
  METHOD_CLASSNAMES,
  PUBLIC_FOLDER,
  RM_DRAWER_MENU_ITEMS,
} from '../../consts.js';
import { fullDayFormatter } from '../../utils/dateFormatters.js';

import styles from '../styles.css';
import { v1Endpoints, v2Endpoints } from '../../apis/kappa.js';
import globalErrorHandler from '../../utils/errorHandler.js';
import { removeFromList } from '../../utils/endpointUtils.js';
import { route } from 'preact-router';

const DRAWER_WIDTH = 480;

function getDrawerState(state) {
  return state.drawer;
}

const Drawer = styled(
  MuiDrawer,
  {}
)(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    whiteSpace: 'nowrap',
    width: DRAWER_WIDTH,
    maxWidth: '100vw',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
  },
  ...(!open && {
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9),
    },
  }),
}));

function renderRebelmouseMenu(menuItem) {
  const { caption, href } = menuItem;
  return (
    <ListItemButton component={'a'} href={href}>
      <ListItemText>{caption}</ListItemText>
    </ListItemButton>
  );
}

function getEndpointList(store) {
  return store.endpoints.list;
}

function getNavigation(store) {
  return store.navigation;
}

function EndpointRecord(props) {
  const { endpointRecord, setDeleteModel } = props;
  const isV2 = endpointRecord.version === 'v2';
  const title = isV2
    ? endpointRecord.displayName || endpointRecord.name
    : `/${endpointRecord.name}`;

  const onDeleteClick = useCallback(
    function onDeleteClick(event) {
      event.preventDefault();
      event.stopPropagation();
      setDeleteModel(endpointRecord);
      return false;
    },
    [endpointRecord]
  );

  return (
    <>
      <ListItem
        sx={{
          py: 0,
        }}
      >
        <ReactiveItemLink
          href={`/${endpointRecord.version}/edit/${endpointRecord._id}`}
        >
          <Stack
            direction={'row'}
            flexGrow={1}
            alignItems={'center'}
            maxWidth={'100%'}
          >
            <ListItemText
              sx={{ flexGrow: 1 }}
              primary={
                <span class={styles.endpointListItem}>
                  <span
                    class={`${styles.methodBlock} ${
                      METHOD_CLASSNAMES[endpointRecord.method]
                    }`}
                  >
                    {endpointRecord.method}
                  </span>

                  {title}

                  <a
                    href={`/r/kappa/api/${endpointRecord.version}/reader/${endpointRecord.name}`}
                    target="_blank"
                    class={styles.link}
                  >
                    <Outbound fontSize="small" className={styles.anchor} />
                  </a>
                </span>
              }
              secondary={
                <>
                  <Typography
                    sx={{ display: 'inline' }}
                    component="span"
                    variant="body2"
                  >
                    {isV2 && `/${endpointRecord.name} | `}Last used{' '}
                  </Typography>
                  {endpointRecord.lastUsed
                    ? fullDayFormatter(endpointRecord.lastUsed)
                    : 'Never'}
                </>
              }
            />

            <IconButton
              aria-label="Delete"
              sx={{ width: 48, height: 48 }}
              onClick={onDeleteClick}
            >
              <Delete />
            </IconButton>
          </Stack>
        </ReactiveItemLink>
      </ListItem>
      <Divider sx={{ my: 1 }} />
    </>
  );
}

function renderEndpoint(setDeleteModel) {
  function render(endpointRecord) {
    return h(EndpointRecord, {
      key: endpointRecord._id,
      endpointRecord,
      setDeleteModel,
    });
  }

  return render;
}

function Sidebar(_props) {
  const dispatch = useDispatch();

  const [deleteModel, setDeleteModel] = useState({});
  const [loading, setLoading] = useState(false);

  const open = useSelector(getDrawerState);
  const endpoints = useSelector(getEndpointList);
  const navigationParams = useSelector(getNavigation);
  const deleteTitle =
    deleteModel.version === 'v2'
      ? deleteModel.displayName || deleteModel.name
      : deleteModel.name;

  const closeDrawer = useCallback(
    function closeDrawer() {
      dispatch(actions.CLOSE());
    },
    [dispatch]
  );

  const cancelDelete = useCallback(function cancelDelete() {
    setDeleteModel({});
  }, []);

  const proceedDelete = useCallback(
    function proceedDelete() {
      setLoading(true);

      function onResponse() {
        removeFromList(deleteModel.version || 'v1', deleteModel._id);

        if (navigationParams.endpointId === deleteModel._id) {
          route(`${PUBLIC_FOLDER}/`, true);
        }
      }

      function onEnd() {
        setLoading(false);
        setDeleteModel({});
      }

      const action =
        deleteModel.version === 'v1' ? v1Endpoints.remove : v2Endpoints.remove;

      action(deleteModel._id)
        .then(onResponse)
        .catch(globalErrorHandler)
        .finally(onEnd);
    },
    [deleteModel, navigationParams.endpointId]
  );

  return (
    <>
      <Drawer
        open={open}
        onClose={closeDrawer}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <List component="nav">
          {RM_DRAWER_MENU_ITEMS.map(renderRebelmouseMenu)}

          <Divider />

          <ReactiveItemLink href="/v2">
            <ListItemText primary="New v2 endpoint" />
          </ReactiveItemLink>

          <Divider sx={{ my: 1 }} />

          {endpoints['v2'].map(renderEndpoint(setDeleteModel))}

          <ReactiveItemLink href="/v1">
            <ListItemText primary="New v1 endpoint (deprecated)" />
          </ReactiveItemLink>

          <Divider sx={{ my: 1 }} />

          {endpoints['v1'].map(renderEndpoint(setDeleteModel))}
        </List>
      </Drawer>
      <Dialog
        open={deleteModel._id != null}
        onClose={cancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Delete {deleteTitle} endpoint?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Once deleted the code cannot be recovered. Disable the endpoint
            instead if you think you will use it again later.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={proceedDelete}
            disabled={loading}
            endIcon={loading ? <CircularProgress /> : null}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default memo(Sidebar);
